export const cars = [
  {
    id: "systems-suv",
    name: "Systems SUV",
    class: "Bare Metal / Embedded",
    basePrice: 0,
    image: "https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?auto=format&fit=crop&q=80&w=1200",
    description: "Zero-std Rust chassis with Candle core. Memory-safe, perpetual license, zero telemetry. Complies with Rule 1: Bare Metal Genesis.",
    stats: { speed: 60, handling: 75, aiCore: 55 },
    ascii: `     ___________
    /  |   |    \\
   /___|___|_____\\
  |   BSM-C 4x4   |
  |  [O]     [O]  |
  |    _______    |
   \\__/       \\__/
    |  | | |  |
   [SHADOW]<>[LIGHT]`
  },
  {
    id: "ai-sports",
    name: "AI Sports Car",
    class: "Neurosymbolic / High-Perf",
    basePrice: 2500,
    image: "https://images.unsplash.com/photo-1544636331-e26879cd4d9b?auto=format&fit=crop&q=80&w=1200",
    description: "Doppelganger V8 engine on carbon BSM-C chassis. Local LLM fuel, strict Rule 2 Shadow Validation sandboxing.",
    stats: { speed: 92, handling: 70, aiCore: 88 },
    ascii: `        ______
       /      \\
      /   ||   \\
     |    ||    |
     |   BSM-C  |
     |  [O] [O] |
      \\   ===   /
       \\______/
   [SHADOW]<>[LIGHT]`
  },
  {
    id: "enterprise-sedan",
    name: "Enterprise Sedan",
    class: "Regulatory / GMP Compliant",
    basePrice: 10000,
    image: "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&q=80&w=1200",
    description: "21 CFR Part 11 validated. Full Eduba indexing. Built for Rule 5: Hammurabi Handoff cryptographic state promotion.",
    stats: { speed: 55, handling: 85, aiCore: 95 },
    ascii: `    _____________
   /             \\
  |     BSM-C     |
  |   [O]     [O] |
  |     =====     |
   \\_____________/
    | |       | |
  [SHADOW]<>[LIGHT]`
  }
];

export const configOptions = {
  engines: [
    { id: 'candle', name: 'Candle Core', desc: 'Rust-native, zero-API', boost: { aiCore: 0, speed: 0 }, price: 0 },
    { id: 'local-llm', name: 'Local LLM', desc: 'Phi-3.5 / Qwen quantized', boost: { aiCore: 25, speed: 5 }, price: 500 },
    { id: 'neuro', name: 'Neurosymbolic', desc: 'Neural + Z3 solver (Rule 4)', boost: { aiCore: 45, speed: 15 }, price: 1500 },
  ],
  storage: [
    { id: 'sqlite', name: 'SQLite + sqlx', desc: 'Atomic, Part 11 ready', boost: { handling: 5 }, price: 0 },
    { id: 'vec', name: 'sqlite-vec', desc: 'Local Embeddings', boost: { handling: 15, aiCore: 10 }, price: 300 },
    { id: 'iedb', name: 'Eduba Semantic Cache', desc: 'Rule 3: Memory Sovereignty', boost: { handling: 25, aiCore: 20 }, price: 800 },
  ],
  validation: [
    { id: 'shadow', name: 'Shadow Code', desc: 'Atomic rollback', boost: { handling: 0 }, price: 0 },
    { id: 'doppelganger', name: 'Doppelganger Sandbox', desc: 'Rule 2: Pre-execution verify', boost: { handling: 10 }, price: 1000 },
    { id: 'handoff', name: 'Hammurabi Handoff', desc: 'Rule 5: Cryptographic proof', boost: { handling: 20 }, price: 2500 },
  ],
  interface: [
    { id: 'terminal', name: 'Terminal', desc: 'CLI-first, minimal', boost: { speed: 5 }, price: 0 },
    { id: 'tauri', name: 'Tauri Desktop', desc: 'Rust + SvelteKit', boost: { speed: 10, handling: 5 }, price: 0 },
    { id: 'static', name: 'Static Site', desc: 'Zero-JS, sovereign', boost: { speed: 15 }, price: 0 },
  ]
};

export const autosDecode = [
  { letter: 'A', word: 'Atomic', desc: 'Indivisible operations, zero partial state' },
  { letter: 'U', word: 'Unified', desc: 'Shadow & light in perfect sync' },
  { letter: 'T', word: 'Traceable', desc: 'Immutable audit, cryptographic proof' },
  { letter: 'O', word: 'Operational', desc: 'Sovereign execution, local-first' },
  { letter: 'S', word: 'Sovereign', desc: 'No telemetry, no external calls' },
];

export const autosProtocolDecode = [
  { letter: 'A', word: 'AI', desc: 'Artificial Intelligence' },
  { letter: 'U', word: 'Universal', desc: 'Open access, no gatekeepers' },
  { letter: 'T', word: 'Tokenomics', desc: 'Asset-backed value distribution' },
  { letter: 'O', word: 'Operational', desc: 'Real utility, not speculation' },
  { letter: 'S', word: 'Sovereignty', desc: 'You own the mint, you own the yield' },
];

export const bsmcRules = [
  { rule: 1, name: 'Bare Metal Genesis', title: 'Start at the Source', desc: 'All data, logic, and state must originate and execute on local hardware. The cloud is a deployment target, never a dependency. If it cannot run locally, it does not run at all.', icon: 'FiHardDrive' },
  { rule: 2, name: 'Shadow Validation', title: 'The Doppelganger Sandbox', desc: 'Every operation runs in a parallel shadow state (The Doppelganger Sandbox). Unverified code never touches the light. Simulation must always precede execution.', icon: 'FiCopy' },
  { rule: 3, name: 'Memory Sovereignty', title: 'The Eduba Cache', desc: 'Data is strictly owned by the sovereign node. The Eduba index processes semantic meaning locally without exposing raw records to external systems.', icon: 'FiDatabase' },
  { rule: 4, name: 'Core Alignment', title: 'Neurosymbolic Truth', desc: 'Probabilistic AI heuristics are bound by deterministic symbolic logic solvers. The AI proposes, but deterministic logic enforces the boundaries.', icon: 'FiCpu' },
  { rule: 5, name: 'Hammurabi Handoff', title: 'Cryptographic Accountability', desc: 'Promoting shadow to light requires cryptographic signatures. An eye for an eye: developers and systems are strictly accountable for the state they commit.', icon: 'FiKey' },
  { rule: 6, name: 'Semantic Evolution', title: 'Continuous Local Learning', desc: 'The system evolves through local embedding updates, maintaining strict privacy while continuously increasing cohort intelligence.', icon: 'FiTrendingUp' },
];

export const architectureLayers = [
  { layer: 'User-Minted Medallions', mechanism: 'The user acts as the sovereign mint, packaging hyper-accurate, contextual data streams into an NSTF container.', reality: 'No mining pools, no gas fees, no volatile spikes. Pure cryptographic proof of data quality.' },
  { layer: 'Corporate Acquisition', mechanism: 'Enterprise buyers purchase access to high-fidelity data pools to train AI and target clean ad placements.', reality: 'Bypasses predatory tech middlemen (Google/Meta ad tax) to funnel enterprise fiat directly to the source.' },
  { layer: 'BSM-C Data Security', mechanism: 'Local-first architecture strips all PII before broadcast, protecting digital sovereignty while maximizing signal value.', reality: 'Sovereign data broadcast protocols ensure you lease utility without surrendering identity.' },
  { layer: 'The Yield Goal', mechanism: 'Aggregate enterprise data contracts split equitably across active, verified sovereign medallion nodes.', reality: 'Target: $1,000/month min in stable, real-world utility distribution.' },
];

export const complianceLayers = [
  { name: 'PII Stripping', status: 'LOCAL', detail: 'All personal identifiers removed before broadcast via Doppelganger Sandbox' },
  { name: 'GDPR Alignment', status: 'VERIFIED', detail: 'Right to erasure baked into Medallion lifecycle' },
  { name: 'CCPA Compliance', status: 'ACTIVE', detail: 'Opt-out signals honored at the node level' },
  { name: '21 CFR Part 11', status: 'READY', detail: 'E-signature binding via Hammurabi Handoff' },
  { name: 'SOC 2 Type II', status: 'PENDING', detail: 'Enterprise gateway certification in progress via Eduba' },
];

export const valueLoopSteps = [
  { id: 1, label: 'USER MINTING', title: 'Generate Sovereign Data', desc: 'Locally generated, high-quality, verified data bundles produced on your metal, under your control.', icon: 'FiCpu', color: 'cyan', detail: 'No cloud dependency. No third-party SDKs. Your node produces the raw signal.' },
  { id: 2, label: 'MEDALLION ENTRY', title: 'Bind to Hammurabi Medallion', desc: 'Data is cryptographically bound to a User-Minted NSTF container — proof of quality and origin.', icon: 'FiAward', color: 'amber', detail: 'Each Medallion is a debt-free token of exchange. No mining pools. No gas fees.' },
  { id: 3, label: 'BSM-C BROADCAST', title: 'Secure Data Broadcast', desc: 'Zero-PII, high-signal data is securely broadcast to the marketplace via sovereign protocols.', icon: 'FiRadio', color: 'fuchsia', detail: 'PII stripped locally before broadcast. You lease utility, never surrender identity.' },
  { id: 4, label: 'CORP PURCHASE', title: 'Enterprise Acquisition', desc: 'B2B companies purchase clean data pools using real fiat ad dollars — bypassing predatory middlemen.', icon: 'FiDollarSign', color: 'emerald', detail: 'Direct settlement. No Google/Meta ad tax. Enterprise fiat flows to the source.' },
  { id: 5, label: '$1,000/MO FLOOR', title: 'Sovereign Yield', desc: 'Non-crypto settlement stream distributed directly to verified sovereign medallion nodes.', icon: 'FiTrendingUp', color: 'amber', detail: 'Target: $1,000/month minimum per active, verified sovereign node. Real income, not speculation.' },
];